import type { ChartType, SecondaryDimensionValues } from '@revelio/core';

import type { PrimaryFilterId } from '../../../filters/types/filters';
import { ChartFallback } from '../../Chart/ChartFallback';
import type { ResolvedData } from '../../DataResolver/resolve-items';
import { useCompositionData } from '../composition/useCompositionData';
import { usePostingsData } from '../postings/usePostingsData';
import type {
  DataProviderChildren,
  SingleDataSet,
  TableData,
  TimeseriesDataSet,
} from '../types';
import { combineQueryStatuses } from '../utils/combine-query-statuses';
import { getEntityMonthlyValues, getEntitySeries } from '../utils/table-data';
import {
  computeDemandSupplyRatio,
  computeDemandSupplyTimeseries,
  computeLatestDemandSupplyRatio,
} from './convert-demand-supply';

type DemandSupplyData = SingleDataSet | TimeseriesDataSet | undefined;

const tableHeadcounts = (table: TableData | undefined): Map<string, number> =>
  new Map(
    (table ? getEntitySeries(table, 'headcount') : []).flatMap(
      ({ value, values }) => {
        const latest = values[values.length - 1];
        return latest === undefined ? [] : [[value.id, latest] as const];
      }
    )
  );

const tableMonthlyHeadcounts = (
  table: TableData | undefined
): Map<string, Map<string, number>> =>
  new Map(
    (table ? getEntityMonthlyValues(table, 'headcount') : []).map(
      ({ value, byMonth }) => [value.id, byMonth]
    )
  );

export const DemandSupplyDataProvider = ({
  primaryDimension,
  secondaryDimension,
  metric,
  chartType,
  headcounts,
  children,
}: {
  primaryDimension: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  metric: 'demand_supply' | 'demand_supply_percentile';
  chartType: ChartType;
  headcounts?: ResolvedData;
  children: DataProviderChildren<DemandSupplyData>;
}) => {
  const isDSRTimeseries =
    chartType === 'line' ||
    chartType === 'volume' ||
    secondaryDimension === 'timeseries';

  const {
    compositionData,
    monthRefs,
    status: compositionStatus,
  } = useCompositionData(primaryDimension, {
    pause: headcounts !== undefined,
  });
  const {
    postingsData,
    weekRefs,
    status: postingsStatus,
  } = usePostingsData(primaryDimension);
  const status = combineQueryStatuses([
    headcounts?.status ?? compositionStatus,
    postingsStatus,
  ]);
  const postingData = postingsData?.entities ?? [];

  const data = (() => {
    if (!headcounts) {
      return computeDemandSupplyRatio({
        compositionData: compositionData?.entities ?? [],
        postingData,
        isDSRTimeseries,
        metric,
        weekRefs,
        monthRefs,
      });
    }
    return isDSRTimeseries
      ? computeDemandSupplyTimeseries({
          headcounts: tableMonthlyHeadcounts(headcounts.data),
          postingData,
          weekRefs,
          metric,
        })
      : computeLatestDemandSupplyRatio({
          headcounts: tableHeadcounts(headcounts.data),
          postingData,
          metric,
        });
  })();

  return (
    <ChartFallback status={status}>{children({ data, status })}</ChartFallback>
  );
};
